import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { EmptyState } from '@/components/ui';

/**
 * Empty plan: explain the model, then offer the two ways in.
 *
 * Adding one task is the primary action, with capture beside it as the
 * secondary. Capture led at first — a plan often exists as a list somewhere,
 * and pasting beats typing ten rows — but it is the bigger, stranger step to
 * put in front of someone whose plan is empty, and the empty state is now the
 * *only* set of actions on the page (the toolbar's pair is hidden here), so it
 * should lead with the simplest one.
 *
 * The shape is `EmptyState`, shared with Financials so the two cannot drift.
 */
export type EmptyPlanStateProps = {
  onStartPlanning?: () => void;
  onCaptureTasks?: () => void;
};

export const EmptyPlanState = ({ onStartPlanning, onCaptureTasks }: EmptyPlanStateProps) => {
  const { t } = useTranslation('pmPlanning');
  return (
    <EmptyState
      automationId="empty-plan"
      icon="checklist"
      title={t('emptyState.Title')}
      body={t('emptyState.Body')}
    >
      <Button
        variant="contained"
        onClick={onStartPlanning}
        startIcon={<MaterialSymbol name="add" size={17} />}
        data-automation-id="empty-plan-start-button"
        sx={{ px: 2, py: 1 }}
      >
        {t('emptyState.AddOneTask')}
      </Button>
      <Button
        variant="outlined"
        color="secondary"
        onClick={onCaptureTasks}
        startIcon={<MaterialSymbol name="edit_note" size={17} />}
        data-automation-id="empty-plan-capture-button"
        sx={{ px: 2, py: 1 }}
      >
        {t('planPanel.CaptureTasks')}
      </Button>
    </EmptyState>
  );
};
